import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { MagnifyingGlass, ClockCounterClockwise, Package, ArrowRight, XCircle, Wrench } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function History() {
  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('serial') || '');
  const [tire, setTire] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [scrapRecord, setScrapRecord] = useState(null);
  const [totalKm, setTotalKm] = useState(0);
  const [loading, setLoading] = useState(false);

  const runSearch = useCallback(async (term) => {
    if (!term?.trim() || !profile) return;
    setLoading(true);
    setTire(null); setTimeline([]); setScrapRecord(null);
    try {
      const { data: tireData, error: tireError } = await supabase
        .from('tires').select('*')
        .eq('company_id', profile.company_id).eq('serial_number', term.trim())
        .maybeSingle();
      if (tireError) throw tireError;
      if (!tireData) { toast.error('Ban tidak ditemukan'); setLoading(false); return; }
      setTire(tireData);

      const { data: positions, error: posError } = await supabase
        .from('tire_positions').select('*, trucks (truck_number)')
        .eq('tire_id', tireData.id).order('mounted_date', { ascending: true });
      if (posError) throw posError;
      setTimeline(positions || []);

      const kmSum = (positions || []).reduce((sum, p) => {
        if (p.mounted_km && p.dismounted_km) return sum + (p.dismounted_km - p.mounted_km);
        return sum;
      }, 0);
      setTotalKm(kmSum);

      if (tireData.status === 'scrap') {
        const { data: scrap } = await supabase
          .from('scrap_records').select('*').eq('tire_id', tireData.id).maybeSingle();
        setScrapRecord(scrap);
      }
    } catch (error) {
      console.error('Error searching tire history:', error);
      toast.error(error.message || 'Pencarian gagal');
    } finally { setLoading(false); }
  }, [profile]);

  // Auto-run search when arriving via ?serial=XYZ link
  useEffect(() => {
    const initial = searchParams.get('serial');
    if (initial && profile) runSearch(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchTerm.trim()) return;
    setSearchParams({ serial: searchTerm.trim() });
    runSearch(searchTerm);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'stock': return <Package size={20} weight="bold" className="text-zinc-600" />;
      case 'mounted': return <ArrowRight size={20} weight="bold" className="text-zinc-900" />;
      case 'scrap': return <XCircle size={20} weight="bold" className="text-red-600" />;
      case 'repair': return <Wrench size={20} weight="bold" className="text-amber-600" />;
      default: return null;
    }
  };

  const statusLabel = (status) => ({
    stock: 'Stok', mounted: 'Terpasang', repair: 'Diperbaiki', scrap: 'Rusak'
  }[status] || status);

  return (
    <div className="p-4 sm:p-8" data-testid="history-page">
      <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-widest text-zinc-900 mb-8" style={{ fontFamily: 'Chivo' }}>
        RIWAYAT BAN
      </h1>

      <form onSubmit={handleSearch} className="bg-white border border-zinc-200 p-6 mb-8 shadow-sm" data-testid="search-form">
        <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Cari berdasarkan Nomor Seri</label>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Masukkan nomor seri lengkap..."
              className="w-full bg-white border border-zinc-300 p-3 pl-10 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
              data-testid="search-input" />
          </div>
          <button type="submit" disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 transition-colors disabled:opacity-50"
            data-testid="search-button">
            {loading ? 'MENCARI...' : 'CARI'}
          </button>
        </div>
      </form>

      {tire && (
        <>
          <div className="bg-white border border-zinc-200 p-6 mb-8 shadow-sm" data-testid="tire-info">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Nomor Seri</p>
                <p className="text-2xl font-mono text-zinc-900 tracking-tight">{tire.serial_number}</p>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 border border-zinc-200">
                {getStatusIcon(tire.status)}
                <span className="text-sm font-mono uppercase tracking-wider text-zinc-900">{statusLabel(tire.status)}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-zinc-200 pt-4">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Tipe</p>
                <p className="text-sm text-zinc-900 font-mono">{tire.type}</p>
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Ukuran</p>
                <p className="text-sm text-zinc-900 font-mono">{tire.size}</p>
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Tapak Awal</p>
                <p className="text-sm text-zinc-900 font-mono">{tire.tread_depth}mm</p>
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Total KM Berjalan</p>
                <p className="text-sm text-zinc-900 font-mono">{totalKm.toLocaleString('id-ID')} KM</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 p-6 shadow-sm" data-testid="timeline">
            <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-2 mb-6" style={{ fontFamily: 'Chivo' }}>
              LINI MASA SIKLUS HIDUP
            </h2>
            <div className="space-y-4">
              <div className="flex gap-4 pb-4 border-b border-zinc-200" data-testid="event-purchased">
                <Package size={24} weight="bold" className="text-zinc-600 shrink-0 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-zinc-900 font-mono font-bold uppercase tracking-wider">MASUK STOK</p>
                  <p className="text-xs text-zinc-600 font-mono mt-1">
                    Ditambahkan ke inventaris · Tapak {tire.tread_depth}mm
                  </p>
                  <p className="text-xs text-zinc-500 font-mono mt-1">
                    {new Date(tire.purchase_date).toLocaleDateString('id-ID')}
                  </p>
                </div>
              </div>

              {timeline.map((event, idx) => (
                <div key={event.id} className="flex gap-4 pb-4 border-b border-zinc-200" data-testid={`event-${idx}`}>
                  <ArrowRight size={24} weight="bold" className="text-zinc-900 shrink-0 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-zinc-900 font-mono font-bold uppercase tracking-wider">
                      DIPASANG · TRUK {event.trucks?.truck_number} · POS {event.position_number}
                    </p>
                    <p className="text-xs text-zinc-600 font-mono mt-1">
                      Tapak Saat Pasang: {event.tread_depth_on}mm · KM Saat Pasang: {event.mounted_km?.toLocaleString('id-ID')}
                    </p>
                    <p className="text-xs text-zinc-500 font-mono mt-1">
                      {new Date(event.mounted_date).toLocaleString('id-ID')}
                    </p>
                    {event.dismounted_date && (
                      <div className="mt-3 pt-3 border-t border-zinc-100">
                        <p className="text-xs text-zinc-900 font-mono uppercase tracking-wider">↳ DILEPAS</p>
                        <p className="text-xs text-zinc-600 font-mono mt-1">
                          Tapak Saat Lepas: {event.tread_depth_off}mm · KM Saat Lepas: {event.dismounted_km?.toLocaleString('id-ID')} · KM Berjalan: {(event.dismounted_km - event.mounted_km).toLocaleString('id-ID')}
                        </p>
                        <p className="text-xs text-zinc-500 font-mono mt-1">
                          {new Date(event.dismounted_date).toLocaleString('id-ID')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {scrapRecord && (
                <div className="flex gap-4" data-testid="event-scrapped">
                  <XCircle size={24} weight="bold" className="text-red-600 shrink-0 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-red-600 font-mono font-bold uppercase tracking-wider">DIRUSAK / SCRAP</p>
                    <p className="text-xs text-zinc-600 font-mono mt-1">
                      Kerusakan: {scrapRecord.damage_types?.join(', ') || 'N/A'}
                    </p>
                    <p className="text-xs text-zinc-600 font-mono">
                      Total KM Berjalan: {scrapRecord.km_total?.toLocaleString('id-ID') || 0}
                    </p>
                    {scrapRecord.notes && (
                      <p className="text-xs text-zinc-600 font-mono mt-1 italic">"{scrapRecord.notes}"</p>
                    )}
                    <p className="text-xs text-zinc-500 font-mono mt-1">
                      {new Date(scrapRecord.scrapped_at).toLocaleString('id-ID')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!tire && !loading && (
        <div className="text-center py-20">
          <ClockCounterClockwise size={64} weight="bold" className="text-zinc-300 mx-auto mb-4" />
          <p className="text-zinc-500 font-mono text-sm">Masukkan nomor seri untuk melacak siklus hidup lengkapnya</p>
        </div>
      )}
    </div>
  );
}
